import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";

const UserModal = ({ show, onClose, user, APIServices, headers, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: "",
    user_login: "",
    role: "USER",
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ================= Fill form when editing ================= */
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        user_login: user.user_login || "",
        role: user.user_role || "USER",
        is_active: user.is_active ?? true,
      });
    } else {
      setFormData({
        username: "",
        user_login: "",
        role: "USER",
        is_active: true,
      });
    }
    setErrors({});
  }, [user, show]);

  /* ================= Validation ================= */
  const validate = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }

    if (!formData.user_login.trim()) {
      newErrors.user_login = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.user_login)) {
      newErrors.user_login = "Invalid email address";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= Submit ================= */
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      let response;

      if (user) {
        response = await APIServices.put(
          `admin/users/${user.user_id}`,
          formData,
          headers
        );
      } else {
        response = await APIServices.post(
          `admin/users/create-user`,
          formData,
          headers
        );
      }

      if (response?.status === 200 || response?.status === 201) {
        onSuccess(response?.data?.message ?? response?.data?.status);
        onClose();
      }
    } catch (error) {
      console.error("Save user error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="user_modal comman_model"
    >
      <Modal.Header closeButton>
        <h1 className="font_16 font-500 clr_blue">
          {user ? "Edit User" : "Create User"}
        </h1>
      </Modal.Header>

      <Modal.Body>
        <Form noValidate onSubmit={handleSubmit}>
          {/* Username */}
          <Form.Group className="form-floating mb-3">
            <Form.Control
              type="text"
              placeholder="Username"
              value={formData.username}
              isInvalid={!!errors.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
            />
            <Form.Label>
              Username <span className="required-star">*</span>
            </Form.Label>
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Email */}
          <Form.Group className="form-floating mb-3">
            <Form.Control
              type="email"
              placeholder="Email"
              value={formData.user_login}
              isInvalid={!!errors.user_login}
              onChange={(e) =>
                setFormData({ ...formData, user_login: e.target.value })
              }
            />
            <Form.Label>
              Email <span className="required-star">*</span>
            </Form.Label>
            <Form.Control.Feedback type="invalid">
              {errors.user_login}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Role */}
          <Form.Group className="form-floating mb-3">
            <Form.Select
              value={formData.role}
              isInvalid={!!errors.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              <option value="USER">User</option>
              <option value="ADMIN">Admin</option>
            </Form.Select>
            <Form.Label>
              Role <span className="required-star">*</span>
            </Form.Label>
            <Form.Control.Feedback type="invalid">
              {errors.role}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Active Checkbox (Edit only) */}
          {user && (
            <Form.Group className="form-check mt-2">
              <Form.Check
                type="checkbox"
                id="isActive"
                label="Active"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    is_active: e.target.checked,
                  })
                }
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button className="btn btn-cancel sky_border" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="btn sky_theme btn-sm"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading
            ? user
              ? "Updating..."
              : "Creating..."
            : user
            ? "Update"
            : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;
